import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import Loader from "../../elements/Loader";
import Pagination from "../../elements/Pagination";

const TH = {
  padding: "10px 14px", fontSize: "11px", fontWeight: 600, color: "#6b7280",
  textTransform: "uppercase", letterSpacing: "0.4px",
  borderBottom: "1px solid #e9ecef", whiteSpace: "nowrap", background: "#f9fafb",
};
const TD = { padding: "10px 14px", verticalAlign: "middle", borderBottom: "1px solid #f5f5f5" };

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleString("en-GB", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  });
}

export default function Dakshinas() {
  const [rows, setRows]             = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [pageCount, setPageCount]   = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [search, setSearch]         = useState("");
  const [fromDate, setFromDate]     = useState("");
  const [toDate, setToDate]         = useState("");
  const [loadingIds, setLoadingIds] = useState(new Set());
  const [paidIds, setPaidIds]       = useState(new Set());

  const LIMIT = 20;

  const fetchList = useCallback(() => {
    setLoading(true);
    let url = `booking/dakshina-list?page=${currentPage + 1}&limit=${LIMIT}&search=${encodeURIComponent(search)}`;
    if (fromDate) url += `&fromDate=${fromDate}`;
    if (toDate)   url += `&toDate=${toDate}`;

    ApiService.get(url)
      .then(d => {
        setLoading(false);
        if (d?.result) {
          setRows(d.result.data);
          setTotalItems(d.result.totalItems);
          setPageCount(d.result.totalPages);
        }
      })
      .catch(() => { setLoading(false); toast.error("Failed to fetch dakshina records"); });
  }, [currentPage, search, fromDate, toDate]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const markAsPaid = async (bookingId) => {
    setLoadingIds(prev => new Set(prev).add(bookingId));
    try {
      await ApiService.patch(`booking/dakshina/${bookingId}/mark-paid`);
      setPaidIds(prev => new Set(prev).add(bookingId));
      toast.success("Dakshina marked as paid");
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to mark as paid";
      toast.error(msg);
    } finally {
      setLoadingIds(prev => { const s = new Set(prev); s.delete(bookingId); return s; });
    }
  };

  const totalPromised = rows.reduce((s, r) => s + (r.promisedDakshina || 0), 0);
  const totalPaid     = rows.reduce((s, r) => s + (r.dakshinaPaid     || 0), 0);

  return (
    <div>
      {loading && <Loader />}

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <h4 style={{ fontWeight: 700, color: "#111827", margin: "0 0 2px" }}>Dakshinas</h4>
        <p style={{ color: "#6b7280", fontSize: "13px", margin: 0 }}>Dakshina paid to pandits for completed pujas</p>
      </div>

      {/* Summary Cards */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { label: "Total Records",      value: totalItems,    color: "#3b82f6", bg: "#eff6ff", icon: "fa-list",        isCount: true },
          { label: "Promised (Page)",    value: totalPromised, color: "#f97316", bg: "#fff7ed", icon: "fa-clock-o" },
          { label: "Actual Paid (Page)", value: totalPaid,     color: "#22c55e", bg: "#f0fdf4", icon: "fa-check-circle" },
        ].map(({ label, value, color, bg, icon, isCount }) => (
          <div key={label} style={{
            flex: "1 1 160px", background: bg, border: `1px solid ${color}30`,
            borderRadius: 10, padding: "14px 16px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className={`fa ${icon}`} style={{ color, fontSize: 15 }} />
              </div>
              <span style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500 }}>{label}</span>
            </div>
            <div style={{ fontSize: "22px", fontWeight: 700, color }}>
              {isCount ? value : `₹${(value || 0).toLocaleString("en-IN")}`}
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: "#fff", border: "1px solid #e9ecef", borderRadius: 10, overflow: "hidden" }}>

        {/* Toolbar */}
        <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <h5 style={{ margin: 0, fontWeight: 700, fontSize: "15px", color: "#111827", marginRight: "auto" }}>
            All Dakshinas
            <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: 400, marginLeft: 6 }}>({totalItems})</span>
          </h5>
          <input
            className="form-control form-control-sm"
            style={{ width: 200 }}
            placeholder="Search pandit, puja, booking..."
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(0); }}
          />
          <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={fromDate} onChange={e => { setFromDate(e.target.value); setCurrentPage(0); }} />
          <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={toDate}   onChange={e => { setToDate(e.target.value);   setCurrentPage(0); }} />
          {(fromDate || toDate) && (
            <button className="btn btn-outline-secondary btn-sm" onClick={() => { setFromDate(""); setToDate(""); setCurrentPage(0); }}>Clear</button>
          )}
        </div>

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <table className="table mb-0" style={{ fontSize: "13px" }}>
            <thead>
              <tr>
                <th style={TH}>Booking ID</th>
                <th style={TH}>Pandit</th>
                <th style={TH}>Puja</th>
                <th style={TH}>Puja Date</th>
                <th style={TH}>Completed On</th>
                <th style={TH}>Promised (₹)</th>
                <th style={TH}>Actual Paid (₹)</th>
                <th style={TH}>Diff (₹)</th>
                <th style={TH}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? rows.map(r => {
                const diff = r.dakshinaPaid != null && r.promisedDakshina != null
                  ? r.dakshinaPaid - r.promisedDakshina
                  : null;
                return (
                  <tr key={r.bookingId}>
                    <td style={TD}>
                      <Link to={`/edit-booking/${r.bookingId}`} style={{ fontFamily: "monospace", fontSize: "11px", color: "#3b82f6" }}>
                        {r.bookingId}
                      </Link>
                    </td>
                    <td style={TD}>
                      <div style={{ fontWeight: 500, color: "#111827" }}>{r.panditName}</div>
                      <div style={{ fontSize: "11px", color: "#9ca3af" }}>{r.panditPhone}</div>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: "12px", color: "#374151" }}>{r.pujaName}</span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>{formatDate(r.pujaDateTime)}</span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>{formatDate(r.completedAt)}</span>
                    </td>
                    <td style={TD}>
                      <span style={{ fontWeight: 500, color: "#374151" }}>
                        {r.promisedDakshina != null ? `₹${Number(r.promisedDakshina).toLocaleString("en-IN")}` : "—"}
                      </span>
                    </td>
                    <td style={TD}>
                      {r.dakshinaPaid != null
                        ? <span style={{ fontWeight: 600, color: "#22c55e" }}>₹{Number(r.dakshinaPaid).toLocaleString("en-IN")}</span>
                        : <span style={{ color: "#f97316", fontSize: "11px" }}>Not entered</span>
                      }
                    </td>
                    <td style={TD}>
                      {diff != null ? (
                        <span style={{ fontWeight: 500, color: diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#6b7280" }}>
                          {diff > 0 ? "+" : ""}{Number(diff).toLocaleString("en-IN")}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={TD}>
                      {r.dakshinaPaid != null ? (
                        paidIds.has(r.bookingId) ? (
                          <span style={{ fontSize: "11px", fontWeight: 600, color: "#22c55e" }}>
                            <i className="fa fa-check-circle" style={{ marginRight: 4 }} />Paid
                          </span>
                        ) : (
                          <button
                            className="btn btn-sm"
                            style={{ fontSize: "11px", background: "#22c55e", color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px" }}
                            disabled={loadingIds.has(r.bookingId)}
                            onClick={() => markAsPaid(r.bookingId)}
                          >
                            {loadingIds.has(r.bookingId) ? "..." : "Mark as Paid"}
                          </button>
                        )
                      ) : (
                        <span style={{ fontSize: "11px", color: "#d1d5db" }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={8} className="text-center py-4" style={{ color: "#9ca3af" }}>
                    No completed bookings with pandits found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{ padding: "10px 16px", borderTop: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "12px", color: "#6b7280" }}>Showing {rows.length} of {totalItems} records</span>
          {pageCount > 1 && <Pagination pageCount={pageCount} handlePageClick={({ selected }) => setCurrentPage(selected)} />}
        </div>
      </div>
    </div>
  );
}
